import { useEffect, useRef } from 'react'
import { useFlag } from '@afterpaytouch/feature'
import { ConvergenceAuthMethod } from '../../model'
import { Features } from '../../model/features'
import { useJourneyId } from '../../state/cashAuth/hooks'
import { CASH_AUTH_SIGNALS, USER_JOURNEY_NAMES, USER_JOURNEY_TIMEOUT_MS } from './constants'
import { markJourneyMarkerHandoff, readJourneyMarker } from './journeyMarker'
import { JourneyOutcome, JourneyTerminationReason } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

/**
 * Cash grant handoff (CASHDAP-3409): the Cash-cohort flow leaves checkout via a
 * same-tab `window.location.replace` to the Cash-owned grant ceremony and returns to
 * `/cash-auth?token=...` as a fresh page load. Without special handling that handoff
 * reads as abandonment: pagehide completes the journey PROCESS_KILLED and nothing
 * restarts it on return. Instead, the redirect call site marks the handoff here just
 * before navigating — the pagehide anchor skips completion, the sessionStorage marker
 * survives (annotated `handoff`), and the return page load resumes the same journey
 * instance from the marker. A customer who never returns leaves no terminal event and
 * is counted analytically (started − terminal), like any closed tab.
 */
let grantHandoffPending = false

/**
 * Call synchronously, immediately before navigating to the Cash grant ceremony.
 * Pending is provisional — it is dropped again if the departing document turns out to
 * live on: restored from the bfcache (persisted pageshow, below), or never unloaded at
 * all — the mobile deep link opens the Cash app over this page and the grant then
 * settles right here via the poll (cleared by the settlement effect in
 * useCashAuthJourneyAnchors). Left pending on a live document, the pagehide anchor
 * would stay suppressed and every marker write would re-assert the handoff annotation,
 * silencing a later real abandonment twice over.
 */
export const beginCashGrantHandoff = (): void => {
  grantHandoffPending = true
  markJourneyMarkerHandoff()
  // location.replace drops this page's history entry, so the browser should never
  // show this document again — but if it does come back from the bfcache (persisted
  // pageshow), the handoff didn't happen and a later pagehide is a real abandonment
  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) {
        grantHandoffPending = false
      }
    },
    { once: true }
  )
}

export const isCashGrantHandoffPending = (): boolean => grantHandoffPending

/** Test-only: module state would otherwise leak between specs */
export const resetCashGrantHandoffForTesting = (): void => {
  grantHandoffPending = false
}

export interface CashAuthJourneyAnchorProps {
  /** The grant came back approved — the journey continues to the standard confirm path */
  isAuthorized: boolean
  /** The customer declined the grant in the Cash ceremony; close() follows and completes USER_CANCELED */
  isDeclined: boolean
  /** The cash-auth polling limit tripped without a grant decision (customer walked away) */
  isPollingLimitExceeded: boolean
  /** The chosen handoff method (desktop/mobile redirect or QR), once known */
  cashAuthMethod?: ConvergenceAuthMethod
}

/**
 * Journey anchors for the cash-auth page (the rocketship-owned segments of the $Pay
 * flow). Resumes a journey handed off to the Cash grant ceremony, tags the cash-auth
 * correlation id and handoff method, and records the grant outcome:
 * - authorized → tag only; SUCCEEDED still comes from the checkout Confirmed transition
 * - declined → friction; the close() that follows completes USER_CANCELED (GOOD with
 *   friction, mirroring the business-decline mapping)
 * - polling limit → friction + TERMINATED (UNKNOWN in the SLI — the customer walked
 *   away, not a system failure). Real cash query errors are NOT completed here: they
 *   route to the terminal-error page whose anchor maps them FAILED. Note the UX still
 *   shows /oh-no for the polling limit too (CashAuth merges it into `error`,
 *   pre-existing) — the journey is already TERMINATED by then, so the /oh-no anchor
 *   no-ops and the classification holds.
 * A resumed journey keeps its original `created`, so the 10-minute timeout deadline is
 * absolute across the handoff — a ceremony that outlives it times out on return,
 * enforced synchronously by the resume effect itself: the timeout anchor cannot arm
 * until the render after the resume, and a rehydrated decline close()s in the same
 * effect flush, so leaving the deadline to the anchor would record USER_CANCELED for a
 * journey that had already expired.
 */
export const useCashAuthJourneyAnchors = ({ isAuthorized, isDeclined, isPollingLimitExceeded, cashAuthMethod }: CashAuthJourneyAnchorProps): void => {
  const enabled = useFlag(Features.USER_JOURNEY_INSTRUMENTATION)
  const { active, addFriction, addTag, completeJourney, resumeJourney } = useUserJourneyContext()
  const cashAuthJourneyId = useJourneyId()
  const resumed = useRef(false)

  const journeyActive = active?.name === USER_JOURNEY_NAMES.CHECKOUT

  useEffect(() => {
    if (!enabled || resumed.current) {
      return
    }
    resumed.current = true
    const marker = readJourneyMarker()
    if (marker?.handoff === true && marker.journey.name === USER_JOURNEY_NAMES.CHECKOUT && !journeyActive) {
      resumeJourney(marker.journey, marker.esid)
      // A DECLINED that was fetched and persisted on the departing page (the page stays
      // alive post-handoff; redux-persist flushes the fulfilled poll entry) rehydrates
      // through PersistGate, so isDeclined can already be true in this first commit. The
      // declined-friction effect below reads this render's journeyActive (still false)
      // and skips, and CashAuth's declined effect close()s via the stateRef before it
      // can re-run — the friction must ride the same synchronous dispatch chain as the
      // resume or it is lost with the completed journey. The normal decline path (poll
      // resolves after the resume re-render) still goes through the effect below.
      if (isDeclined && !marker.journey.frictions.includes(CASH_AUTH_SIGNALS.DECLINED)) {
        addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
      }
      // Absolute-deadline enforcement, on the same synchronous dispatch chain as the
      // resume (see the hook doc comment). Friction first so it rides the completed
      // journey — TERMINATED with friction still classifies UNKNOWN; no frustration is
      // added, so an expired ceremony never dips the SLI. The declined close() (and any
      // later completion) then no-ops in the reducer. IDLE_USER by the framework's
      // late-fire semantics: the deadline passed while the customer was away in the
      // Cash ceremony.
      if (Date.now() - marker.journey.created > USER_JOURNEY_TIMEOUT_MS) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, JourneyTerminationReason.TIMEOUT_WITH_IDLE_USER)
      }
    }
  }, [enabled, journeyActive, resumeJourney, isDeclined, addFriction, completeJourney])

  useEffect(() => {
    // Same-document settlement: on mobile the deep link opens the Cash app without
    // unloading this page, so the grant decision arrives here via the poll rather than
    // on a fresh return load — the handoff is over the moment it settles. Ordered
    // before the tag and declined-friction effects so the settlement commit's own
    // dispatch already rewrites the marker un-annotated (see beginCashGrantHandoff).
    if (isAuthorized || isDeclined) {
      grantHandoffPending = false
    }
  }, [isAuthorized, isDeclined])

  useEffect(() => {
    if (!journeyActive || active === undefined) {
      return
    }
    const tagOnce = (key: string, value: string | undefined): void => {
      if (value === undefined || value === '' || active.tags.some((tag) => tag.startsWith(`${key}:`))) {
        return
      }
      addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
    }
    tagOnce('cashAuthJourneyId', cashAuthJourneyId)
    tagOnce('cashAuthMethod', cashAuthMethod)
    if (isAuthorized) {
      tagOnce('cashAuthResult', 'authorized')
    }
  }, [journeyActive, active, addTag, cashAuthJourneyId, cashAuthMethod, isAuthorized])

  useEffect(() => {
    if (isDeclined && journeyActive && active?.frictions.includes(CASH_AUTH_SIGNALS.DECLINED) === false) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.DECLINED)
    }
  }, [isDeclined, journeyActive, active, addFriction])

  useEffect(() => {
    if (isPollingLimitExceeded && journeyActive) {
      addFriction(USER_JOURNEY_NAMES.CHECKOUT, CASH_AUTH_SIGNALS.TIMEOUT)
      completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED)
    }
  }, [isPollingLimitExceeded, journeyActive, addFriction, completeJourney])
}
